import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { reservationService, MyReservation } from 'services/reservationService';
import {
  Body,
  Header,
  BackButton,
  Content,
  FacilityImageSection,
  FacilityImage,
  FacilityImagePlaceholder,
  InfoCard,
  FacilityNameRow,
  FacilityName,
  ViewDetailLink,
  LocationInfo,
  LocationIcon,
  Divider,
  DetailRow,
  DetailLabel,
  DetailValue,
  TimeDisplay,
  TimeDivider,
  MemberInfo,
  MemberImage,
  MemberDetails,
  MemberName,
  LoadingContainer,
  ErrorMessage,
  DescriptionSection,
  SectionTitle,
  SectionContent,
  IconText
} from './ReservationDetail.styles';

const ReservationDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [reservation, setReservation] = useState<MyReservation | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchReservationDetail = async () => {
      if (!id) {
        setError('예약 ID가 없습니다.');
        setIsLoading(false);
        return;
      }

      try {
        const reservation = await reservationService.getReservationById(parseInt(id));
        setReservation(reservation);
      } catch (error) {
        console.error('Error fetching reservation detail:', error);
        setError('예약 정보를 불러오는데 실패했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReservationDetail();
  }, [id]);

  const handleGoBack = () => {
    navigate(-1);
  };


  const handleViewFacilityDetail = () => {
    if (reservation) {
      navigate(`/facilities/${reservation.facility.id}`);
    }
  };

  const formatDateTime = (isoString: string): { date: string; time: string } => {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const time = `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;

    return {
      date: `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`,
      time: time
    };
  };

  const calculateEndTime = (startTime: string, durationMinutes: number): string => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    const hours = end.getHours();
    const minutes = end.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;
  };

  const getDayOfWeek = (isoString: string): string => {
    const date = new Date(isoString);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return days[date.getDay()];
  };

  if (isLoading) {
    return (
      <Body>
        <Header>
          <BackButton onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          예약 상세
        </Header>
        <LoadingContainer>로딩 중...</LoadingContainer>
      </Body>
    );
  }

  if (error || !reservation) {
    return (
      <Body>
        <Header>
          <BackButton onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          예약 상세
        </Header>
        <ErrorMessage>{error || '예약을 찾을 수 없습니다.'}</ErrorMessage>
      </Body>
    );
  }

  const startDateTime = formatDateTime(reservation.startTime);
  const endTime = calculateEndTime(reservation.startTime, reservation.durationMinutes);
  const dayOfWeek = getDayOfWeek(reservation.startTime);
  const locationText = [
    reservation.facility.building?.name,
    reservation.facility.location
  ].filter(Boolean).join(' ');

  return (
    <Body>
      <Header>
        <BackButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </BackButton>
        예약 상세
      </Header>

      <Content>
        {reservation.facility.imageUrl ? (
          <FacilityImageSection onClick={handleViewFacilityDetail}>
            <FacilityImage src={reservation.facility.imageUrl} alt={reservation.facility.name} />
          </FacilityImageSection>
        ) : (
          <FacilityImageSection onClick={handleViewFacilityDetail}>
            <FacilityImagePlaceholder>🏢</FacilityImagePlaceholder>
          </FacilityImageSection>
        )}

        <InfoCard>
          <FacilityNameRow>
            <FacilityName>{reservation.facility.name}</FacilityName>
            <ViewDetailLink onClick={handleViewFacilityDetail}>
              상세보기 ›
            </ViewDetailLink>
          </FacilityNameRow>
          {locationText && (
            <LocationInfo>
              <LocationIcon>📍</LocationIcon>
              {locationText}
            </LocationInfo>
          )}

          <Divider />

          <DetailRow>
            <DetailLabel>예약 날짜</DetailLabel>
            <DetailValue>{startDateTime.date} ({dayOfWeek})</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailLabel>예약 시간</DetailLabel>
            <TimeDisplay>
              <DetailValue>{startDateTime.time}</DetailValue>
              <TimeDivider>~</TimeDivider>
              <DetailValue>{endTime}</DetailValue>
              <span style={{ color: '#999', fontSize: '14px', marginLeft: '8px' }}>
                ({reservation.durationMinutes}분)
              </span>
            </TimeDisplay>
          </DetailRow>

          <DetailRow>
            <DetailLabel>인원</DetailLabel>
            <DetailValue>
              <IconText>👥</IconText>
              {reservation.headCount}명
            </DetailValue>
          </DetailRow>
        </InfoCard>

        {reservation.facility.description && (
          <DescriptionSection>
            <SectionTitle>시설 설명</SectionTitle>
            <SectionContent>{reservation.facility.description}</SectionContent>
          </DescriptionSection>
        )}

        {reservation.facility.purpose && (
          <DescriptionSection>
            <SectionTitle>용도</SectionTitle>
            <SectionContent>{reservation.facility.purpose}</SectionContent>
          </DescriptionSection>
        )}

        {reservation.facility.equipment && (
          <DescriptionSection>
            <SectionTitle>보유 기자재</SectionTitle>
            <SectionContent>{reservation.facility.equipment}</SectionContent>
          </DescriptionSection>
        )}

        {reservation.facility.caution && (
          <DescriptionSection>
            <SectionTitle>주의사항</SectionTitle>
            <SectionContent>{reservation.facility.caution}</SectionContent>
          </DescriptionSection>
        )}

        <InfoCard>
          <SectionTitle>예약자 정보</SectionTitle>
          <MemberInfo>
            <MemberImage>
              {reservation.member.profileImage ? (
                <img
                  src={reservation.member.profileImage}
                  alt={reservation.member.name}
                  style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                />
              ) : (
                '👤'
              )}
            </MemberImage>
            <MemberDetails>
              <MemberName>{reservation.member.name}</MemberName>
            </MemberDetails>
          </MemberInfo>
        </InfoCard>
      </Content>
    </Body>
  );
};

export default ReservationDetail;
